import React, { useState } from 'react';
import { Mail, ShieldCheck, Zap, ChevronRight, ChevronLeft, CheckCircle2, AlertCircle, Plus, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { moduleService } from '../../../services/modules';
import { MiniAppShell } from './MiniAppShell';

interface EmailAppProps {
    onExecutionStarted: (executionId: string) => void;
    onBack: () => void;
}

export const EmailApp = ({ onExecutionStarted, onBack }: EmailAppProps) => {
    const navItems = [
        { id: 'wizard', label: 'Setup Wizard', icon: Zap },
        { id: 'aliases', label: 'Alias Manager', icon: Mail },
        { id: 'deliverability', label: 'Deliverability', icon: ShieldCheck },
    ];

    return (
        <MiniAppShell
            title="Email Service"
            icon={Mail}
            navItems={navItems}
            onBack={onBack}
        >
            {(activeTab, setActiveTab) => {
                switch (activeTab) {
                    case 'wizard':
                        return <SetupWizard onExecutionStarted={onExecutionStarted} />;
                    case 'aliases':
                        return <AliasManager />;
                    case 'deliverability':
                        return <DeliverabilityHub />;
                    default:
                        return <SetupWizard onExecutionStarted={onExecutionStarted} />;
                }
            }}
        </MiniAppShell>
    );
};

const SetupWizard = ({ onExecutionStarted }: { onExecutionStarted: (id: string) => void }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [params, setParams] = useState({
        domain: '',
        aliases: '',
        smtpPassword: '',
        forwardingEmail: '',
    });

    const steps = [
        { id: 1, label: 'Domain', icon: Globe },
        { id: 2, label: 'Aliases', icon: Mail },
        { id: 3, label: 'SMTP', icon: Zap },
        { id: 4, label: 'Verify', icon: CheckCircle2 },
    ];

    const handleFinalize = async () => {
        setLoading(true);
        try {
            const result = await moduleService.executeModule('email_setup', {
                ...params,
                requestedAt: new Date().toISOString(),
            });
            onExecutionStarted(result.executionId);
            alert('Email setup triggered successfully!');
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto space-y-8">
            <div className="flex items-center justify-between mb-8">
                <div className="flex gap-4">
                    {steps.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                                step === s.id ? 'bg-slate-900 text-white scale-110 shadow-md' :
                                step > s.id ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                            }`}>
                                {step > s.id ? <CheckCircle2 size={14} /> : s.id}
                            </div>
                            {i < steps.length - 1 && <div className={`h-px w-8 ${step > s.id ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-8">
                {step === 1 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="space-y-2">
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Connect Your Domain</h3>
                            <p className="text-xs text-slate-400 font-medium">Enter the domain you want to use for your professional emails.</p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Domain Name</label>
                            <input
                                value={params.domain}
                                onChange={(e) => setParams({ ...params, domain: e.target.value })}
                                placeholder="mybusiness.com"
                                className="w-full px-6 py-4 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-2 ring-indigo-500 outline-none transition-all bg-slate-50"
                            />
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="space-y-2">
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Create Your Aliases</h3>
                            <p className="text-xs text-slate-400 font-medium">Define the email addresses you want (e.g. hello, info, sales).</p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Comma Separated Aliases</label>
                            <input
                                value={params.aliases}
                                onChange={(e) => setParams({ ...params, aliases: e.target.value })}
                                placeholder="hello, info, support"
                                className="w-full px-6 py-4 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-2 ring-indigo-500 outline-none transition-all bg-slate-50"
                            />
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="space-y-2">
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Configure Outgoing (SMTP)</h3>
                            <p className="text-xs text-slate-400 font-medium">Set up your 'Send As' capabilities using Gmail or another provider.</p>
                        </div>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">App Password</label>
                                <input
                                    type="password"
                                    value={params.smtpPassword}
                                    onChange={(e) => setParams({ ...params, smtpPassword: e.target.value })}
                                    placeholder="••••••••••••"
                                    className="w-full px-6 py-4 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-2 ring-indigo-500 outline-none transition-all bg-slate-50"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Forwarding Address</label>
                                <input
                                    value={params.forwardingEmail}
                                    onChange={(e) => setParams({ ...params, forwardingEmail: e.target.value })}
                                    placeholder="your-personal@gmail.com"
                                    className="w-full px-6 py-4 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-2 ring-indigo-500 outline-none transition-all bg-slate-50"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {step === 4 && (
                    <div className="text-center space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="p-6 bg-indigo-50 rounded-full text-indigo-600 w-20 h-20 mx-auto flex items-center justify-center">
                            <ShieldCheck size={40} />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">Verification Phase</h3>
                            <p className="text-sm text-slate-400 font-medium mx-auto max-w-xs">
                                We'll now verify your DNS records (MX, SPF, DKIM) to ensure maximum deliverability.
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between pt-8">
                    <button
                        onClick={() => setStep(s => Math.max(1, s - 1))}
                        disabled={step === 1}
                        className="px-6 py-3 text-slate-400 hover:text-slate-800 transition-all text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-0"
                    >
                        <ChevronLeft size={14} /> Previous
                    </button>
                    {step < 4 ? (
                        <button
                            onClick={() => setStep(s => s + 1)}
                            className="px-8 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2"
                        >
                            Next Step <ChevronRight size={14} />
                        </button>
                    ) : (
                        <button
                            onClick={handleFinalize}
                            disabled={loading}
                            className="px-8 py-3 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Activate Email Ecosystem'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const AliasManager = () => {
    const mockAliases = [
        { id: '1', email: 'hello@acme-corp.com', forwardTo: 'admin@gmail.com', status: 'Active' },
        { id: '2', email: 'support@acme-corp.com', forwardTo: 'support@gmail.com', status: 'Active' },
        { id: '3', email: 'billing@acme-corp.com', forwardTo: 'finance@gmail.com', status: 'Pending' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Alias Manager</h2>
                    <p className="text-xs text-slate-400 font-medium">Create and route custom business email addresses</p>
                </div>
                <button className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-slate-800 transition-all">
                    <Plus size={14} /> New Alias
                </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
                {mockAliases.map(alias => (
                    <div key={alias.id} className="p-4 bg-white border border-slate-100 rounded-2xl shadow-sm flex items-center justify-between group hover:border-indigo-500 transition-all">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                                <Mail size={16} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-700">{alias.email}</p>
                                <p className="text-[10px] text-slate-400 font-medium">Forwards to: {alias.forwardTo}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                alias.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                            }`}>
                                {alias.status}
                            </span>
                            <button className="p-2 text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DeliverabilityHub = () => {
    const checks = [
        { id: 'mx', label: 'MX Records', status: 'Pass', description: 'Mail exchange servers are correctly pointed.' },
        { id: 'spf', label: 'SPF Record', status: 'Pass', description: 'Sender Policy Framework is authorized.' },
        { id: 'dkim', label: 'DKIM Signature', status: 'Warning', description: 'Public key found but signature mismatch detected.' },
        { id: 'dmarc', label: 'DMARC Policy', status: 'Fail', description: 'No DMARC record found. Risk of spam flagging.' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Deliverability Hub</h2>
                    <p className="text-xs text-slate-400 font-medium">Ensure your emails land in the inbox, not the spam folder</p>
                </div>
                <button className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-slate-800 transition-all">
                    <RefreshCw size={14} /> Re-Verify DNS
                </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {checks.map(check => (
                    <div key={check.id} className="p-5 bg-white border border-slate-100 rounded-2xl shadow-sm flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${
                            check.status === 'Pass' ? 'bg-emerald-50 text-emerald-600' :
                            check.status === 'Warning' ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600'
                        }`}>
                            {check.status === 'Pass' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-bold text-slate-700">{check.label}</h3>
                                <span className={`text-[10px] font-black uppercase tracking-wider ${
                                    check.status === 'Pass' ? 'text-emerald-600' :
                                    check.status === 'Warning' ? 'text-amber-600' : 'text-rose-600'
                                }`}>
                                    {check.status}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 font-medium mt-1">{check.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Global helper for globe icon
const Globe = ({ size, className }: { size: number, className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M2 12h20"></path>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </svg>
);
